// frontend/src/context/ConfigContext.tsx - Updated to only handle round names, scores come from pools
import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { getTournamentConfig, saveTournamentConfig } from '../api/api';

// Now only contains round name configuration
export interface RoundConfig {
  name: string;
}

interface ConfigContextType {
  roundConfigs: RoundConfig[];
  setRoundConfigs: (configs: RoundConfig[]) => void;
  getRoundName: (round: number) => string;
  isLoading: boolean;
  saveConfigToAPI: () => Promise<void>;
  // Method for updating a single round name
  updateRoundName: (roundIndex: number, newName: string) => Promise<void>;
  // Method to apply scoring from pool data
  applyPoolScoring: (pointValues: number[]) => void;
  // Current point values (comes from pool data)
  currentPointValues: number[];
}

// Default round configurations - Round of 64 is not editable for scoring
const defaultRoundConfigs: RoundConfig[] = [
  { name: "Round of 32" },    // Index 0 (was index 1)
  { name: "Round of 16" },    // Index 1 (was index 2)
  { name: "Round of 8" },     // Index 2 (was index 3)
  { name: "Round of 4" },     // Index 3 (was index 4)
  { name: "Championship Game" }, // Index 4 (was index 5)
  { name: "Champion" },       // Index 5 (was index 6)
];

// Default point values (6 rounds)
const defaultPointValues: number[] = [1, 2, 4, 8, 16, 32];

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const ConfigProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [roundConfigs, setRoundConfigs] = useState<RoundConfig[]>(defaultRoundConfigs);
  const [currentPointValues, setCurrentPointValues] = useState<number[]>(defaultPointValues);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Load configuration from API on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true);
        const savedConfig = await getTournamentConfig();
        
        // Validate the loaded config has the right structure (6 rounds)
        if (Array.isArray(savedConfig) && savedConfig.length === 6) {
          const isValidConfig = savedConfig.every(config => 
            config && 
            typeof config === 'object' && 
            typeof config.name === 'string'
          );
          
          if (isValidConfig) {
            setRoundConfigs(savedConfig);
          } else {
            console.warn('Invalid saved config structure, using defaults');
            setRoundConfigs(defaultRoundConfigs);
          }
        } else {
          console.warn('Invalid saved config length, using defaults');
          setRoundConfigs(defaultRoundConfigs);
        }
      } catch (error) {
        console.error('Error loading tournament config:', error);
        // Keep default configuration on error
        setRoundConfigs(defaultRoundConfigs);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, []);
  
  // Function to save configuration to API
  const saveConfigToAPI = async (): Promise<void> => {
    try {
      await saveTournamentConfig(roundConfigs);
    } catch (error) {
      console.error('Error saving tournament config:', error);
      throw error;
    }
  };

  // Function to update a single round name and save to API
  const updateRoundName = async (roundIndex: number, newName: string): Promise<void> => {
    if (roundIndex < 0 || roundIndex >= roundConfigs.length) {
      throw new Error('Invalid round index');
    }

    if (!newName.trim()) {
      throw new Error('Round name cannot be empty');
    }

    try {
      // Update the round configs
      const updatedConfigs = [...roundConfigs];
      updatedConfigs[roundIndex] = {
        name: newName.trim()
      };
      
      // Update context
      setRoundConfigs(updatedConfigs);
      
      // Save to API immediately
      await saveTournamentConfig(updatedConfigs);
    } catch (error) {
      console.error('Error updating round name:', error);
      throw error;
    }
  };

  // Function to apply scoring from pool data
  const applyPoolScoring = (pointValues: number[]) => {
    if (pointValues.length === 6) {
      setCurrentPointValues(pointValues);
    } else {
      console.warn('Invalid point values length, expected 6 values');
      setCurrentPointValues(defaultPointValues);
    }
  };
  
  // Function to get round name based on index (0-6 now)
  const getRoundName = (round: number): string => {
    if (round >= 0 && round < roundConfigs.length) {
      return roundConfigs[round]?.name || `Round ${round}`;
    }
    
    // Default fallback
    return `Round ${round}`;
  };
  
  return (
    <ConfigContext.Provider value={{ 
      roundConfigs, 
      setRoundConfigs, 
      getRoundName, 
      isLoading,
      saveConfigToAPI,
      updateRoundName,
      applyPoolScoring,
      currentPointValues
    }}>
      {children}
    </ConfigContext.Provider>
  );
};

// Hook to use the config context
export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};